import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApprovalRequest } from 'src/app/components/proposal-details/models/approval-request.model';
import { ApiMethod } from 'src/app/decorators/api-method.decorator';
import { BriefApprovalProposalRequest } from 'src/app/models/brief-approval-proposal-request.model';
import { BriefComment } from 'src/app/models/brief-comment.model';
import { Job } from 'src/app/models/job.model';
import { BoardProposalItem, CustomerProposalItem } from 'src/app/models/proposal/proposal.model';
import { UserContactInfo } from 'src/app/models/user-contact-info.model';
import { environment } from 'src/environments/environment';
import { Document } from 'src/app/models/document.model';

@Injectable({
	providedIn: 'root'
})
export class ProposalService {

	private baseProposalUrl = `${environment.baseUrl}/proposal`;
	private baseJobUrl = `${environment.baseUrl}/job`;
	private baseBoardUrl = `${environment.baseUrl}/boardmember`;

	constructor(private httpClient: HttpClient) { }

	@ApiMethod()
	getRelevantContacts(token: string): Observable<UserContactInfo[]> {
		return this.httpClient.get<UserContactInfo[]>(`${this.baseProposalUrl}/${token}/relevant-contacts`);
	}

	@ApiMethod()
	getCustomerComments(token: string, proposalId: number): Observable<BriefComment[]> {
		return this.httpClient.get<BriefComment[]>(`${this.baseProposalUrl}/${token}/customer-proposals/${proposalId}/comments`);
	}

	@ApiMethod()
	addCustomerComment(token: string, proposalId: number, comment: string): Observable<void> {
		const formData = new FormData();
		formData.append('comment', comment || '');

		return this.httpClient.post<void>(`${this.baseProposalUrl}/${token}/customer-proposals/${proposalId}/comments`, formData);
	}

	@ApiMethod()
	canPropose(token: string): Observable<boolean> {
		return this.httpClient.get<boolean>(`${this.baseProposalUrl}/${token}/can-propose`);
	}

	@ApiMethod()
	getProposalJobDetails(token: string): Observable<Job> {
		return this.httpClient.get<Job>(`${this.baseJobUrl}/${token}/proposal`);
	}

	@ApiMethod()
	getProposalDocuments(token: string): Observable<Document[]> {
		return this.httpClient.get<Document[]>(`${this.baseProposalUrl}/${token}/documents`);
	}

	@ApiMethod()
	getBoardProposal(token: string): Observable<ApprovalRequest> {
		return this.httpClient.get<ApprovalRequest>(`${this.baseProposalUrl}/${token}/board-proposal`);
	}

	getBoardProposals(token: string): Observable<BoardProposalItem[]> {
		return this.httpClient.get<BoardProposalItem[]>(`${this.baseProposalUrl}/${token}/board-proposals`);
	}

	@ApiMethod()
	voidProposal(token: string, proposalId: number, reason: string): Observable<void> {
		const formData = new FormData();
		formData.append('reason', reason || '');

		return this.httpClient.post<void>(`${this.baseProposalUrl}/${token}/customer-proposals/${proposalId}/cancel`, formData);
	}

	@ApiMethod()
	approveProposal(token: string, proposalDocumentId: number, approvedBudget: number): Observable<void> {
		const formData = new FormData();
		formData.append('approvedBudget', approvedBudget.toString());

		return this.httpClient.post<void>(`${this.baseProposalUrl}/${token}/documents/${proposalDocumentId}/approve`, formData);
	}

	@ApiMethod()
	declineProposal(token: string, proposalId: number, reason: string): Observable<void> {
		const formData = new FormData();
		formData.append('reason', reason || '');

		return this.httpClient.post<void>(`${this.baseProposalUrl}/${token}/decline`, formData);
	}

	@ApiMethod()
	voteProposal(token: string, proposalDocumentId: number): Observable<void> {
		return this.httpClient.post<void>(`${this.baseProposalUrl}/${token}/documents/${proposalDocumentId}/vote`, {});
	}

	@ApiMethod()
	negotiateProposal(token: string, proposalDocumentId: number, comment: string): Observable<void> {
		const formData = new FormData();
		formData.append('comment', comment || '');

		return this.httpClient.post<void>(`${this.baseProposalUrl}/${token}/documents/${proposalDocumentId}/negotiate`, formData);
	}

	@ApiMethod()
	propose(token: string, proposal: BriefApprovalProposalRequest): Observable<boolean> {
		return this.httpClient.post<boolean>(`${this.baseProposalUrl}/${token}/propose`, proposal);
	}

	@ApiMethod()
	getCustomerProposals(token: string): Observable<CustomerProposalItem[]> {
		return this.httpClient.get<CustomerProposalItem[]>(`${this.baseProposalUrl}/${token}/customer-proposals`);
	}

	@ApiMethod()
	getCustomerProposal(token: string, proposalId: number): Observable<ApprovalRequest> {
		return this.httpClient.get<ApprovalRequest>(`${this.baseProposalUrl}/${token}/customer-proposals/${proposalId}`);
	}

	@ApiMethod()
	getBoardMemberComments(token: string): Observable<BriefComment[]> {
		return this.httpClient.get<BriefComment[]>(`${this.baseBoardUrl}/${token}/comments`);
	}

	@ApiMethod()
	addBoardMemberComment(token: string, comment: string): Observable<void> {
		const formData = new FormData();
		formData.append('comment', comment || '');

		return this.httpClient.post<void>(`${this.baseBoardUrl}/${token}/comments`, formData);
	}
}
